import React, { useEffect, useState } from 'react'
import {
  Alert, alpha, Box, Button, Card, CardContent, Container, Fade, Grid, Snackbar, Stack, Tab, Tabs, TextField, Typography
} from '@mui/material'
import {
  Architecture, CheckCircle, DesignServices, Diamond, Email, Palette, Phone, Schedule, Send, Star
} from '@mui/icons-material'
import Cal from '@calcom/embed-react'

const ContactPage = () => {
  const [isVisible, setIsVisible] = useState(false)
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', projectType: '', budget: '', timeline: '', message: ''
  })
  const [formErrors, setFormErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' })
  const [activeTab, setActiveTab] = useState(0)

  useEffect(() => {
    setIsVisible(true)
  }, [])

  const projectTypes = [
    { value: 'residential-full', label: 'Full Home Design', icon: <DesignServices/>, color: '#61533c' },
    { value: 'residential-partial', label: 'Room Redesign', icon: <Palette/>, color: '#8B7355' },
    { value: 'luxury-staging', label: 'Luxury Staging', icon: <Star/>, color: '#352d18' },
    { value: 'consultation', label: 'Design Consultation', icon: <Architecture/>, color: '#473e32' },
    { value: 'other', label: 'Other Project', icon: <Diamond/>, color: '#9e8865' }]

  const handleInputChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev, [field]: event.target.value
    }))

    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev, [field]: ''
      }))
    }
  }

  const validateForm = () => {
    const errors = {}

    if (!formData.name.trim()) errors.name = 'Name is required'
    if (!formData.email.trim()) {
      errors.email = 'Email is required'
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Please enter a valid email'
    }
    if (!formData.projectType) errors.projectType = 'Please select a project type'
    if (!formData.message.trim()) errors.message = 'Please tell us about your vision'

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async (event) => {
    event.preventDefault()

    if (!validateForm()) {
      setSnackbar({
        open: true, message: 'Please fill in all required fields', severity: 'error'
      })
      return
    }

    setIsSubmitting(true)

    try {

      const formDataToSend = new FormData()
      formDataToSend.append('form-name', 'contact')
      formDataToSend.append('name', formData.name)
      formDataToSend.append('email', formData.email)
      formDataToSend.append('phone', formData.phone || 'Not provided')
      formDataToSend.append(
       'project-type', projectTypes.find(p => p.value === formData.projectType)?.label || formData.projectType)
      formDataToSend.append('budget', formData.budget || 'Not specified')
      formDataToSend.append('timeline', formData.timeline || 'Not specified')
      formDataToSend.append('message', formData.message)
      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body:   new URLSearchParams(formDataToSend).toString()
      })

      if (response.ok) {
        setIsSubmitting(false)
        setIsSubmitted(true)

        setSnackbar({
          open: true, message: 'Your message has been sent successfully! Juleen will contact you within 24 hours.',
          severity: 'success'
        })

        setTimeout(() => {
          setIsSubmitted(false)
          setFormData({
            name: '', email: '', phone: '', projectType: '', budget: '', timeline: '', message: ''
          })
        }, 5000)
      } else {
        throw new Error('Form submission failed')
      }

    } catch (error) {
      setIsSubmitting(false)
      setSnackbar({
        open: true, message: 'Sorry, there was an error sending your message. Please try again or contact us directly.',
        severity: 'error'
      })
    }
  }

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false })
  }

  return (<Box sx={{ minHeight: '100vh', position: 'relative', overflow: 'hidden', bgcolor: '#FDFDFB' }}>

    <Box sx={{
      position:   'absolute',
      top:        0,
      left:       0,
      right:      0,
      bottom:     0,
      background: 'linear-gradient(135deg, #FDFDFB 0%, #FAF8F5 50%, #F5F2ED 100%)',
      zIndex:     0,
      overflow:   'hidden'
    }}>

      <Box sx={{
        position:           'absolute',
        top:                '10%',
        right:              '5%',
        width:              '300px',
        height:             '300px',
        border:             `1px solid ${alpha('#675944', 0.08)}`,
        borderRadius:       '2px',
        transform:          'rotate(45deg)',
        animation:          'float 8s ease-in-out infinite',
        '@keyframes float': {
          '0%, 100%': { transform: 'rotate(45deg) translateY(0px)' },
          '50%':      { transform: 'rotate(45deg) translateY(-20px)' }
        }
      }}/>

      <Box sx={{
        position:     'absolute',
        bottom:       '15%',
        left:         '8%',
        width:        '200px',
        height:       '200px',
        border:       `1px solid ${alpha('#675944', 0.05)}`,
        borderRadius: '2px',
        transform:    'rotate(20deg)',
        animation:    'float 6s ease-in-out infinite 1s',
      }}/>


      <Box sx={{
        position:        'absolute',
        top:             0,
        left:            0,
        right:           0,
        bottom:          0,
        backgroundImage: `linear-gradient(${alpha('#675944', 0.03)} 1px, transparent 1px),
                           linear-gradient(90deg, ${alpha('#675944', 0.03)} 1px, transparent 1px)`,
        backgroundSize:  '50px 50px',
        opacity:         0.3
      }}/>
    </Box>


    <Box sx={{
      position:   'relative',
      minHeight:  { xs: '60vh', md: '80vh' },
      display:    'flex',
      alignItems: 'center',
      zIndex:     1,
      py:         { xs: 4, md: 0 }
    }}>
      <Container maxWidth="xl">
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6}>
            <Fade in={isVisible} timeout={800}>
              <Box>
                <Typography variant="h1" sx={{
                  fontWeight:    300,
                  color:         '#1A1A1A',
                  mb:            3,
                  fontSize:      { xs: '2.5rem', sm: '3rem', md: '4rem', lg: '5rem' },
                  fontFamily:    '"Inter", sans-serif',
                  letterSpacing: '-0.02em',
                  lineHeight:    1.1
                }}>
                  Where Your
                  <Box component="span" sx={{
                    display:              'block',
                    background:           'linear-gradient(135deg, #675944 0%, #8B7355 100%)',
                    backgroundClip:       'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor:  'transparent',
                    fontWeight:           400,
                    mt:                   1
                  }}>
                    Vision is Seen
                  </Box>
                </Typography>

                <Typography variant="h6" sx={{
                  color:      '#666',
                  mb:         4,
                  fontWeight: 400,
                  fontFamily: '"Inter", sans-serif',
                  lineHeight: 1.6,
                  maxWidth:   '500px',
                  fontSize:   '1.25rem'
                }}>
                  Transforming spaces through visionary design that reflects your unique
                  perspective and elevates everyday living.
                </Typography>


                <Box sx={{
                  display: 'grid', gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(3, 1fr)' }, gap: 3, mt: 6
                }}>
                  {[
                    { number: '150+', label: 'Visions Realized' },
                    { number: '24h', label: 'Response Time' },
                    { number: '100%', label: 'Client Focused' }].map((stat, index) => (<Box key={index} sx={{
                    textAlign: 'center', p: 3, position: 'relative', '&::before': {
                      content:    '""',
                      position:   'absolute',
                      top:        0,
                      left:       0,
                      right:      0,
                      height:     '1px',
                      background: `linear-gradient(90deg, transparent, ${alpha('#675944', 0.3)}, transparent)`
                    }
                  }}>
                    <Typography variant="h4" sx={{
                      color: '#675944', fontWeight: 300, mb: 1, fontFamily: '"Inter", sans-serif', fontSize: '2rem'
                    }}>
                      {stat.number}
                    </Typography>
                    <Typography variant="body2" sx={{
                      color:         '#666',
                      fontSize:      '0.875rem',
                      fontFamily:    '"Inter", sans-serif',
                      fontWeight:    400,
                      letterSpacing: '0.05em'
                    }}>
                      {stat.label}
                    </Typography>
                  </Box>))}
                </Box>
              </Box>
            </Fade>
          </Grid>


          <Grid item xs={12} md={6}>
            <Fade in={isVisible} timeout={1000}>
              <Box sx={{
                position: 'relative', height: '600px', display: { xs: 'none', md: 'block' }
              }}>

                <Box sx={{
                  position:            'absolute',
                  top:                 '50%',
                  left:                '50%',
                  transform:           'translate(-50%, -50%)',
                  width:               '400px',
                  height:              '400px',
                  border:              `1px solid ${alpha('#675944', 0.1)}`,
                  borderRadius:        '2px',
                  animation:           'rotate 20s linear infinite',
                  '@keyframes rotate': {
                    '0%':   { transform: 'translate(-50%, -50%) rotate(0deg)' },
                    '100%': { transform: 'translate(-50%, -50%) rotate(360deg)' }
                  }
                }}/>

                <Box sx={{
                  position:     'absolute',
                  top:          '50%',
                  left:         '50%',
                  transform:    'translate(-50%, -50%)',
                  width:        '300px',
                  height:       '300px',
                  background:   `linear-gradient(135deg, ${alpha('#675944', 0.05)} 0%, ${alpha('#675944', 0.02)} 100%)`,
                  border:       `1px solid ${alpha('#675944', 0.08)}`,
                  borderRadius: '2px',
                  animation:    'rotate 15s linear infinite reverse'
                }}/>


                <Box sx={{
                  position:       'absolute',
                  top:            '50%',
                  left:           '50%',
                  transform:      'translate(-50%, -50%)',
                  width:          '150px',
                  height:         '150px',
                  display:        'flex',
                  alignItems:     'center',
                  justifyContent: 'center'
                }}>
                  <Box sx={{
                    width:              '100%',
                    height:             '100%',
                    position:           'relative',
                    animation:          'pulse 4s ease-in-out infinite',
                    '@keyframes pulse': {
                      '0%, 100%': { transform: 'scale(1)' }, '50%': { transform: 'scale(1.05)' }
                    }
                  }}>

                    <Box sx={{
                      position:     'absolute',
                      top:          '25%',
                      left:         '25%',
                      width:        '50%',
                      height:       '50%',
                      background:   `linear-gradient(135deg, ${alpha('#675944', 0.1)} 0%, ${alpha(
                       '#675944', 0.05)} 100%)`,
                      border:       `1px solid ${alpha('#675944', 0.1)}`,
                      borderRadius: '4px',
                      transform:    'rotate(45deg)'
                    }}/>
                    <Box sx={{
                      position:     'absolute',
                      top:          '35%',
                      left:         '35%',
                      width:        '30%',
                      height:       '30%',
                      background:   `linear-gradient(135deg, ${alpha('#675944', 0.15)} 0%, ${alpha(
                       '#675944', 0.08)} 100%)`,
                      border:       `1px solid ${alpha('#675944', 0.15)}`,
                      borderRadius: '2px',
                      transform:    'rotate(45deg)'
                    }}/>
                  </Box>
                </Box>
              </Box>
            </Fade>
          </Grid>
        </Grid>
      </Container>
    </Box>

    <Container
     maxWidth="xl"
     sx={{
       py: { xs: 6, sm: 8, md: 12, lg: 15 }, position: 'relative', zIndex: 1
     }}
    >
      <Fade in={isVisible} timeout={1200}>
        <Box>

          <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
            <Typography
             variant="h2"
             sx={{
               fontWeight: 300,
               color:      '#1A1A1A',
               mb:         2,
               fontFamily: '"Inter", sans-serif',
               fontSize:   { xs: '1.75rem', sm: '2.25rem', md: '2.75rem', lg: '3rem' }
             }}
            >
              Begin Your Vision
            </Typography>
            <Typography
             variant="h6"
             sx={{
               color:      '#666',
               fontWeight: 400,
               maxWidth:   { xs: '100%', sm: '80%', md: '60%' },
               mx:         'auto',
               fontFamily: '"Inter", sans-serif',
               fontSize:   { xs: '0.9rem', sm: '0.95rem' }
             }}
            >
              Share your vision with us and let&apos;s create something extraordinary together
            </Typography>
          </Box>

          <Grid container spacing={{ xs: 4, md: 8 }}>

            <Grid item xs={12} lg={8}>
              <Card
               sx={{
                 borderRadius:   '16px',
                 border:         (theme) => `1px solid ${alpha('#675944', 0.08)}`,
                 background:     'linear-gradient(135deg, rgba(253,253,251,0.95) 0%, rgba(250,248,245,0.95) 100%)',
                 backdropFilter: 'blur(12px)',
                 boxShadow:      '0 20px 60px rgba(26,26,26,0.06)',
                 overflow:       'hidden',
                 position:       'relative'
               }}
              >
                <Box
                 sx={{
                   p:            { xs: 3, sm: 4 },
                   borderBottom: (theme) => `1px solid ${alpha('#675944', 0.08)}`,
                   background:   'rgba(255,255,255,0.6)'
                 }}
                >
                  <Typography
                   variant="h4"
                   sx={{
                     fontFamily: '"Inter", sans-serif',
                     fontWeight: 500,
                     color:      '#1A1A1A',
                     mb:         1,
                     fontSize:   { xs: '1.05rem', sm: '1.15rem' }
                   }}
                  >
                    Share Your Vision
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#666', fontSize: { xs: '0.9rem', sm: '0.95rem' } }}>
                    Choose your preferred way to connect
                  </Typography>
                </Box>


                <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'white' }}>
                  <Box
                   sx={{
                     overflowX:              { xs: 'auto', sm: 'visible' },
                     '&::-webkit-scrollbar': { height: 6 },
                     px:                     { xs: 1, sm: 0 }
                   }}
                  >
                    <Tabs
                     value={activeTab}
                     onChange={(e, newValue) => setActiveTab(newValue)}
                     centered={false}
                     variant="scrollable"
                     scrollButtons="auto"
                     sx={{
                       minHeight:                 { xs: 56, sm: 64 }, '& .MuiTab-root': {
                         color:            '#666',
                         fontWeight:       500,
                         fontSize:         { xs: '0.9rem', sm: '1rem' },
                         textTransform:    'none',
                         minHeight:        { xs: 56, sm: 64 },
                         px:               { xs: 1.5, sm: 3 },
                         '&.Mui-selected': {
                           color: '#675944', fontWeight: 600
                         }
                       }, '& .MuiTabs-indicator': {
                         backgroundColor: '#675944', height: 2
                       }
                     }}
                    >
                      <Tab icon={<Send/>} iconPosition="start" label="Send Message"/>
                      <Tab icon={<Schedule/>} iconPosition="start" label="Schedule Consultation"/>
                    </Tabs>
                  </Box>
                </Box>

                <CardContent sx={{ p: { xs: 3, sm: 4 } }}>

                  {activeTab === 0 && (!isSubmitted ? (<Box
                   component="form"
                   name="contact"
                   method="POST"
                   data-netlify="true"
                   onSubmit={handleSubmit}
                  >
                    <input type="hidden" name="form-name" value="contact"/>
                    <Grid container spacing={{ xs: 2, md: 4 }}>
                       <Grid item xs={12} md={6}>
                         <TextField
                          fullWidth
                          name="name"
                          label="Your Name"
                          value={formData.name}
                          onChange={handleInputChange('name')}
                          error={!!formErrors.name}
                          helperText={formErrors.name}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '8px', '&:hover fieldset': {
                                borderColor: '#675944'
                              }
                            }
                          }}
                         />
                       </Grid>
                       <Grid item xs={12} md={6}>
                         <TextField
                          fullWidth
                          name="email"
                          label="Email Address"
                          type="email"
                          value={formData.email}
                          onChange={handleInputChange('email')}
                          error={!!formErrors.email}
                          helperText={formErrors.email}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '8px', '&:hover fieldset': {
                                borderColor: '#675944'
                              }
                            }
                          }}
                         />
                       </Grid>

                       <Grid item xs={12}>
                         <Typography variant="h6" sx={{
                           mb: 3, color: '#1A1A1A', fontFamily: '"Inter", sans-serif'
                         }}>
                           Project Vision
                         </Typography>
                         <Grid container spacing={2}>
                           {projectTypes.map((type) => (<Grid item xs={12} sm={6} md={4} key={type.value}>
                             <Card
                              onClick={() => {
                                setFormData(prev => ({
                                  ...prev, projectType: type.value
                                }))
                                if (formErrors.projectType) {
                                  setFormErrors(prev => ({
                                    ...prev, projectType: ''
                                  }))
                                }
                              }}
                              sx={{
                                cursor:       'pointer',
                                border:       formData.projectType === type.value
                                               ? `2px solid #675944`
                                               : `1px solid ${alpha('#675944', 0.2)}`,
                                bgcolor:      formData.projectType === type.value
                                               ? alpha('#675944', 0.04)
                                               : 'transparent',
                                borderRadius: '8px',
                                transition:   'all 0.3s ease',
                                '&:hover':    {
                                  borderColor: '#675944', transform: 'translateY(-2px)'
                                }
                              }}
                             >
                               <CardContent
                                sx={{ p: 3, textAlign: 'center' }}>
                                 <Typography variant="body1" sx={{
                                   fontWeight: 500, color: '#1A1A1A', fontFamily: '"Inter", sans-serif'
                                 }}>
                                   {type.label}
                                 </Typography>
                               </CardContent>
                             </Card>
                           </Grid>))}
                         </Grid>
                         {formErrors.projectType && (<Alert severity="error"
                                                            sx={{ mt: 2, borderRadius: '8px' }}>
                           {formErrors.projectType}
                         </Alert>)}
                       </Grid>

                       <Grid item xs={12}>
                         <TextField
                          fullWidth
                          name="message"
                          multiline
                          rows={5}
                          label="Describe Your Vision"
                          placeholder="Tell us about your space, your style, and what you hope to achieve..."
                          value={formData.message}
                          onChange={handleInputChange('message')}
                          error={!!formErrors.message}
                          helperText={formErrors.message}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '8px', '&:hover fieldset': {
                                borderColor: '#675944'
                              }
                            }
                          }}
                         />
                       </Grid>

                      <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                         <Button
                          type="submit"
                          variant="contained"
                          size="large"
                          disabled={isSubmitting}
                          sx={{
                            px: { xs: 2.5, sm: 3 }, py: { xs: 0.8, sm: 1 }, fontSize: { xs: '0.85rem', sm: '0.95rem' },
                            borderRadius:    '8px',
                            minWidth:        200,
                            color:           '#FDFDFB',
                            backgroundColor: '#675944',
                            fontWeight:      500,
                            fontFamily:      '"Inter", sans-serif',
                            textTransform:   'none',
                            letterSpacing:   '0.02em',
                            '&:hover':       {
                              backgroundColor: '#574732',
                              transform:       'translateY(-2px)',
                              boxShadow:       '0 8px 25px rgba(103, 89, 68, 0.3)'
                            },
                            '&:disabled':    {
                              backgroundColor: '#E8E2D9', color: '#A8A098'
                            },
                            transition:      'all 0.3s ease'
                          }}
                         >
                           {isSubmitting ? 'Sharing Vision...' : 'Share Your Vision'}
                         </Button>
                       </Grid>
                     </Grid>
                   </Box>) : (<Box sx={{ textAlign: 'center', py: 6 }}>
                    <Box sx={{
                      width:                80,
                      height:               80,
                      borderRadius:         '50%',
                      backgroundColor:      '#675944',
                      display:              'flex',
                      alignItems:           'center',
                      justifyContent:       'center',
                      margin:               '0 auto 32px auto',
                      color:                'white',
                      fontSize:             '2.5rem',
                      animation:            'scaleIn 0.6s ease-out',
                      '@keyframes scaleIn': {
                        '0%':   { transform: 'scale(0)', opacity: 0 },
                        '70%':  { transform: 'scale(1.1)', opacity: 1 },
                        '100%': { transform: 'scale(1)', opacity: 1 }
                      }
                    }}>
                      <CheckCircle fontSize="inherit"/>
                    </Box>
                    <Typography variant="h4" sx={{
                      fontFamily: '"Inter", sans-serif', color: '#1A1A1A', mb: 3, fontWeight: 500
                    }}>
                      Vision Received
                    </Typography>
                    <Typography variant="body1" sx={{
                      color: '#666', mb: 3, lineHeight: 1.6, fontWeight: 400, maxWidth: '500px', margin: '0 auto'
                    }}>
                      Thank you for sharing your vision with us. We'll review your
                      project and contact you within 24 hours to begin our
                      collaboration.
                    </Typography>
                  </Box>))}


                  {activeTab === 1 && (<Box sx={{
                    borderRadius:  '8px',
                    overflow:      'hidden',
                    minHeight:     '600px',
                    background:    'white',
                    border:        `1px solid ${alpha('#675944', 0.1)}`,
                    display:       'flex',
                    flexDirection: 'column',
                    width:         '100%',
                    '& > div':     {
                      flex:                            1, overflowY: 'auto', '&::-webkit-scrollbar': {
                        width: '8px'
                      }, '&::-webkit-scrollbar-track': {
                        background: '#F5F2ED', borderRadius: '4px'
                      }, '&::-webkit-scrollbar-thumb': {
                        background: '#675944', borderRadius: '4px', '&:hover': {
                          background: '#574732'
                        }
                      }
                    },
                    '& iframe':    {
                      borderRadius: '8px', width: '100%', minHeight: '600px !important', border: 'none'
                    }
                  }}>
                    <Cal
                     calLink="imseen-interiors"
                     config={{
                       layout: 'month_view', theme: 'light'
                     }}
                     style={{
                       width: '100%', height: '600px', overflow: 'auto'
                     }}
                    />
                  </Box>)}
                </CardContent>
              </Card>
            </Grid>


            <Grid item xs={12} lg={4}>
              <Box sx={{ position: 'sticky', top: 100 }}>

                <Card sx={{
                  borderRadius:   '16px',
                  border:         `1px solid ${alpha('#675944', 0.1)}`,
                  mb:             4,
                  background:     'linear-gradient(135deg, rgba(253, 253, 251, 0.9) 0%, rgba(250, 248, 245, 0.9) 100%)',
                  backdropFilter: 'blur(20px)'
                }}>
                  <CardContent sx={{ p: 4 }}>
                    <Typography variant="h5" sx={{
                      fontFamily: '"Inter", sans-serif', fontWeight: 500, mb: 4, color: '#1A1A1A'
                    }}>
                      Connect With Us
                    </Typography>

                    <Stack spacing={3}>
                      {[
                        {
                          icon:        <Email sx={{ fontSize: 24 }}/>,
                          title:       'Email',
                          value:       'Juleen@imseen-interiors.com',
                          description: 'Detailed vision discussions'
                        }, {
                          icon:        <Phone sx={{ fontSize: 24 }}/>,
                          title:       'Phone',
                          value:       '+1 (240) 316-1208',
                          description: 'Direct conversations'
                        }, {
                          icon:        <Schedule sx={{ fontSize: 24 }}/>,
                          title:       'Response Time',
                          value:       'Within 24 hours',
                          description: 'Personal attention guaranteed'
                        }].map((contact, index) => (<Box key={index} sx={{
                        display:      'flex',
                        alignItems:   'flex-start',
                        gap:          3,
                        p:            3,
                        borderRadius: '12px',
                        background:   alpha('#675944', 0.03),
                        border:       `1px solid ${alpha('#675944', 0.1)}`,
                        transition:   'all 0.3s ease',
                        '&:hover':    {
                          background: alpha('#675944', 0.05), transform: 'translateX(4px)'
                        }
                      }}>
                        <Box sx={{
                          color:        '#675944',
                          p:            1.5,
                          borderRadius: '8px',
                          background:   alpha('#675944', 0.08),
                          display:      'flex',
                          flexShrink:   0
                        }}>
                          {contact.icon}
                        </Box>
                        <Box>
                          <Typography variant="h6" sx={{
                            color:      '#1A1A1A',
                            mb:         1,
                            fontFamily: '"Inter", sans-serif',
                            fontWeight: 500,
                            fontSize:   '1rem'
                          }}>
                            {contact.title}
                          </Typography>
                          <Typography variant="body1" sx={{
                            fontWeight: 500, mb: 0.5, color: '#675944'
                          }}>
                            {contact.value}
                          </Typography>
                          <Typography variant="caption" sx={{
                            color: '#666', fontFamily: '"Inter", sans-serif'
                          }}>
                            {contact.description}
                          </Typography>
                        </Box>
                      </Box>))}
                    </Stack>
                  </CardContent>
                </Card>


                <Card sx={{
                  borderRadius:   '16px',
                  border:         `1px solid ${alpha('#675944', 0.1)}`,
                  background:     'linear-gradient(135deg, rgba(253, 253, 251, 0.9) 0%, rgba(250, 248, 245, 0.9) 100%)',
                  backdropFilter: 'blur(20px)'
                }}>
                  <CardContent sx={{ p: 4 }}>
                    <Typography variant="h5" sx={{
                      fontFamily: '"Inter", sans-serif', color: '#1A1A1A', mb: 4, fontWeight: 500
                    }}>
                      Our Process
                    </Typography>

                    <Stack spacing={2}>
                      {[
                        {
                          title: 'Vision Discovery', desc: 'Understanding your unique perspective and goals'
                        }, {
                          title: 'Design Development', desc: 'Creating tailored solutions for your space'
                        }, {
                          title: 'Implementation', desc: 'Bringing the design vision to reality'
                        }, { title: 'Final Reveal', desc: 'Presenting your transformed space' }].map(
                       (item, index) => (<Box key={index} sx={{
                         display:      'flex',
                         alignItems:   'center',
                         gap:          2,
                         p:            2.5,
                         borderRadius: '8px',
                         background:   alpha('#675944', 0.03),
                         border:       `1px solid ${alpha('#675944', 0.1)}`,
                         transition:   'all 0.3s ease',
                         '&:hover':    {
                           background: alpha('#675944', 0.05)
                         }
                       }}>
                         <Box sx={{
                           width:          24,
                           height:         24,
                           borderRadius:   '4px',
                           bgcolor:        '#675944',
                           color:          'white',
                           display:        'flex',
                           alignItems:     'center',
                           justifyContent: 'center',
                           fontSize:       '0.75rem',
                           fontWeight:     600,
                           flexShrink:     0
                         }}>
                           {index + 1}
                         </Box>
                         <Box>
                           <Typography variant="body1" sx={{
                             fontWeight: 500, mb: 0.5, color: '#1A1A1A', fontFamily: '"Inter", sans-serif'
                           }}>
                             {item.title}
                           </Typography>
                           <Typography variant="caption" sx={{
                             color: '#666', lineHeight: 1.4, fontFamily: '"Inter", sans-serif'
                           }}>
                             {item.desc}
                           </Typography>
                         </Box>
                       </Box>))}
                    </Stack>
                  </CardContent>
                </Card>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Fade>
    </Container>


    <Snackbar
     open={snackbar.open}
     autoHideDuration={6000}
     onClose={handleCloseSnackbar}
     anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert
       onClose={handleCloseSnackbar}
       severity={snackbar.severity}
       sx={{
         borderRadius: '8px', fontFamily: '"Inter", sans-serif'
       }}
      >
        {snackbar.message}
      </Alert>
    </Snackbar>
  </Box>)
}

export default ContactPage
